import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate, useParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { opportunityQuestionsServices } from '../../services'
import { showBanner } from '../../global-state/banner-slice'
import {
  OpportunityQuestionReplyPresenter,
  type OpportunityQuestionReplyFormValues,
} from './opportunity-question-reply-presenter'
import type { AdminOpportunityQuestion } from '../../services/opportunity-questions-service'

export function OpportunityQuestionReplyContainer() {
  const { questionId } = useParams()
  const navigate = useNavigate()
  const dispatch = useDispatch()

  const form = useForm<OpportunityQuestionReplyFormValues>({
    defaultValues: { answer: '' },
    mode: 'onBlur',
  })

  const [question, setQuestion] = useState<AdminOpportunityQuestion | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const loadQuestion = useCallback(async () => {
    const id = Number(questionId)
    if (!questionId || Number.isNaN(id)) {
      dispatch(showBanner({ text: 'Invalid question', variant: 'error-banner' }))
      navigate('/faqs/opportunity-questions')
      return
    }

    try {
      setIsLoading(true)
      const response = await opportunityQuestionsServices.getQuestion(id)
      if (response.status === 200 && 'question' in response) {
        setQuestion(response.question)
        form.reset({ answer: response.question.answer_text ?? '' })
        return
      }
      dispatch(showBanner({ text: 'Question not found', variant: 'error-banner' }))
      navigate('/faqs/opportunity-questions')
    } catch {
      dispatch(showBanner({ text: 'Failed to load question', variant: 'error-banner' }))
      navigate('/faqs/opportunity-questions')
    } finally {
      setIsLoading(false)
    }
  }, [questionId, dispatch, navigate, form])

  useEffect(() => {
    void loadQuestion()
  }, [loadQuestion])

  const onSubmit = async (data: OpportunityQuestionReplyFormValues) => {
    const id = Number(questionId)
    if (!questionId || Number.isNaN(id)) return

    try {
      setIsSubmitting(true)
      const response = await opportunityQuestionsServices.replyToQuestion(id, data.answer)
      if (response && response.status === 200) {
        dispatch(showBanner({ text: 'Answer saved', variant: 'success-banner' }))
        navigate('/faqs/opportunity-questions')
        return
      }
      dispatch(showBanner({ text: 'Unable to save answer', variant: 'error-banner' }))
    } catch {
      dispatch(showBanner({ text: 'Unable to save answer', variant: 'error-banner' }))
    } finally {
      setIsSubmitting(false)
    }
  }

  const onCancel = () => {
    navigate('/faqs/opportunity-questions')
  }

  return (
    <OpportunityQuestionReplyPresenter
      question={question}
      form={form}
      isLoading={isLoading}
      isSubmitting={isSubmitting}
      onSubmit={onSubmit}
      onCancel={onCancel}
    />
  )
}
